import { DEFAULT_DOOR_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type { WallDoor, WallWindow } from "@gettactic/map-builder/src/types";
import {
  bezierArcLength,
  bezierParameterAtDistance,
  bezierPointAndNormal,
  getWallControlPoint,
  quadraticBezierPoint
} from "@gettactic/map-builder/src/utils/bezier";
import {
  getCurvedWallSegmentParams,
  getWallSegments
} from "@gettactic/map-builder/src/utils/wall";
import React from "react";

export interface WallEdgeProps {
  from: { x: number; y: number };
  to: { x: number; y: number };
  thickness: number;
  color: string;
  dashArray?: string;
  curvature?: number;
  curvatureCenter?: number;
  doors?: WallDoor[];
  windows?: WallWindow[];
  /** Trim at the start/end (corner radius overlap), straight edges only. */
  startOffset?: number;
  endOffset?: number;
}

const windowStrokeWidth = (thickness: number) =>
  thickness - (thickness * 20) / 100;

/**
 * One wall edge: solid segments with gaps for doors, door and window
 * lines, straight or along a quadratic bezier. Shared by `wall` and every
 * edge of a `wall-path`; the geometry mirrors `ViewSVG` exactly.
 */
export const WallEdge: React.FC<WallEdgeProps> = ({
  from,
  to,
  thickness,
  color,
  dashArray,
  curvature = 0,
  curvatureCenter = 0.5,
  doors,
  windows,
  startOffset = 0,
  endOffset = 0
}) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const hasDoors = !!doors && doors.length > 0;
  const lineCap = hasDoors ? "round" : "butt";

  if (curvature === 0) {
    const ux = length > 0 ? dx / length : 0;
    const uy = length > 0 ? dy / length : 0;
    let segments = getWallSegments(doors, length);
    if (startOffset > 0 || endOffset > 0) {
      segments = segments
        .map(
          ([start, end]) =>
            [
              Math.max(start, startOffset),
              Math.min(end, length - endOffset)
            ] as [number, number]
        )
        .filter(([start, end]) => start < end);
    }
    const along = (distance: number) => ({
      x: from.x + distance * ux,
      y: from.y + distance * uy
    });
    return (
      <g>
        {doors?.map((door) => {
          if (length === 0 || door.transparent) return null;
          const start = along(door.position * length - door.width / 2);
          const end = along(door.position * length + door.width / 2);
          return (
            <line
              key={door.id}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={DEFAULT_DOOR_COLOR}
              strokeWidth={thickness / 2}
              strokeLinecap="butt"
            />
          );
        })}
        {segments.map(([start, end]) => {
          const a = along(start);
          const b = along(end);
          return (
            <line
              key={`${start}-${end}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={color}
              strokeWidth={thickness}
              strokeLinecap={lineCap}
              strokeDasharray={dashArray}
            />
          );
        })}
        {windows?.map((window) => {
          if (length === 0) return null;
          const start = along(window.position * length - window.width / 2);
          const end = along(window.position * length + window.width / 2);
          return (
            <line
              key={window.id}
              x1={start.x}
              y1={start.y}
              x2={end.x}
              y2={end.y}
              stroke={DEFAULT_DOOR_COLOR}
              strokeWidth={windowStrokeWidth(thickness)}
              strokeLinecap="butt"
            />
          );
        })}
      </g>
    );
  }

  const { cx, cy } = getWallControlPoint(
    from.x,
    from.y,
    to.x,
    to.y,
    curvature,
    curvatureCenter
  );
  const pointAt = (t: number) =>
    quadraticBezierPoint(from.x, from.y, cx, cy, to.x, to.y, t);
  const arcLength = bezierArcLength(from.x, from.y, cx, cy, to.x, to.y);
  const opening = (position: number, width: number) => {
    const t = bezierParameterAtDistance(
      from.x,
      from.y,
      cx,
      cy,
      to.x,
      to.y,
      position * arcLength
    );
    const { x, y, nx, ny } = bezierPointAndNormal(
      from.x,
      from.y,
      cx,
      cy,
      to.x,
      to.y,
      t
    );
    const half = width / 2;
    return {
      x1: x + half * ny,
      y1: y - half * nx,
      x2: x - half * ny,
      y2: y + half * nx
    };
  };
  const segmentParams = getCurvedWallSegmentParams(
    doors,
    from.x,
    from.y,
    cx,
    cy,
    to.x,
    to.y
  );
  return (
    <g>
      {doors?.map((door) => {
        if (length === 0 || door.transparent) return null;
        const line = opening(door.position, door.width);
        return (
          <line
            key={door.id}
            {...line}
            stroke={DEFAULT_DOOR_COLOR}
            strokeWidth={thickness / 2}
            strokeLinecap="butt"
          />
        );
      })}
      {segmentParams.map(([tStart, tEnd]) => {
        const start = pointAt(tStart);
        const end = pointAt(tEnd);
        const mid = pointAt((tStart + tEnd) / 2);
        // Control point of the sub-curve: C = 2·mid − (start + end)/2.
        const subCx = 2 * mid.x - 0.5 * (start.x + end.x);
        const subCy = 2 * mid.y - 0.5 * (start.y + end.y);
        return (
          <path
            key={`${tStart}-${tEnd}`}
            d={`M ${start.x},${start.y} Q ${subCx},${subCy} ${end.x},${end.y}`}
            stroke={color}
            strokeWidth={thickness}
            strokeLinecap={lineCap}
            strokeDasharray={dashArray}
            fill="none"
          />
        );
      })}
      {windows?.map((window) => {
        if (length === 0) return null;
        const line = opening(window.position, window.width);
        return (
          <line
            key={window.id}
            {...line}
            stroke={DEFAULT_DOOR_COLOR}
            strokeWidth={windowStrokeWidth(thickness)}
            strokeLinecap="butt"
          />
        );
      })}
    </g>
  );
};
